import moment from 'moment';

import { truncateTextWithEllipsis } from '@utilities';

import { surveyDatePromptIds } from '@api/surveys/utilities/constants';

const PILL_DATE_FORMAT = 'MM/DD/YYYY';

/**
 * @typedef {Object} PromptAnswerValue value returned from Insights prompt searching
 * @property {String} attributeId eg. "sector"
 * @property {String} id eg. "sector:isc_apparel"
 * @property {Boolean} is_fixed
 * @property {String} name
 * @property {String} prompt_desc
 */

/**
 * @typedef {Object} PromptAnswer
 * @property {String} field
 * @property {Number|String} [id]
 * @property {Number} promptId
 * @property {PromptAnswerValue[]} values
 */

/**
 * transformPromptSelections
 * @param {Object[]|String[]} options.answers
 * @param {Object} options.prompt
 * @return {PromptAnswer[]}
 */
export const transformPromptSelections = ({ answers, prompt }) => {
  if (!answers || answers.length === 0) return [];
  const isDateRange = surveyDatePromptIds.includes(prompt.insightsKey);

  if (prompt.promptKey === 'comparison_period') {
    return [
      {
        fieldType: 'COMPARISON_PERIOD',
        promptId: prompt?.id,
        values: answers,
      },
    ];
  }

  if (isDateRange) {
    return [
      {
        field: prompt.attributes[0]?.field,
        fieldType: 'DATE',
        operator: 'IN_RANGE',
        promptId: prompt.id,
        values: answers,
      },
    ];
  }

  const isCustomFilterPrompt =
    prompt.insightsKey === 'purchase_volume' || prompt.insightsKey === 'unique_brands';

  if (isCustomFilterPrompt) {
    return answers;
  }

  const defaultField = prompt.attributes?.[0]?.field;
  const promptAnswers = [];
  answers.forEach((answer) => {
    const field =
      answer.field ||
      prompt.attributes?.find(({ id }) => answer.attributeId === id)?.field ||
      defaultField;
    const existingAnswer = promptAnswers.find(
      ({ field: existingField }) => existingField === field
    );
    if (existingAnswer) {
      existingAnswer.operator = 'IN_LIST';
      existingAnswer.values.push(answer);
    } else {
      promptAnswers.push({
        field,
        fieldType: 'TEXT',
        operator: 'EQUALS',
        promptId: prompt.id,
        values: [answer],
      });
    }
  });

  return promptAnswers;
};

/**
 * gets value for pill based on current prompt and answers
 * @param {Object} options
 * @param {Number} options.firstAnswerMaxLength
 * @param {Object} options.prompt
 * @param {PromptAnswer[]} options.promptAnswers group.promptAnswers
 * @return {String}
 */
export const getPromptPillValue = ({ firstAnswerMaxLength = 0, prompt, promptAnswers = [] }) => {
  if (prompt.promptKey === 'comparison_period') {
    if (promptAnswers?.length === 0) return prompt?.emptyValue;
    const selectedIds = promptAnswers?.[0]?.values?.[prompt?.promptKey];
    return prompt?.dateOptions?.find((dateOption) =>
      dateOption.options.some(({ value }) => selectedIds.includes(value[0]))
    )?.timeFrame;
  }

  if (prompt?.insightsKey === 'purchase_volume') {
    const attribute = prompt.attributes?.find(
      (attr) => attr.id === promptAnswers?.[0]?.attributeId
    );
    const name = attribute?.name;
    return name || prompt?.emptyValue;
  }
  const answers = promptAnswers
    .filter(({ _delete, promptId }) => !_delete && promptId === prompt.id)
    .map(({ values }) => values)
    .flat(1);

  if (answers.length === 0) return prompt?.emptyValue;

  const isDateRange = surveyDatePromptIds.includes(prompt.insightsKey);

  if (isDateRange) {
    if (answers.length === 2 && typeof answers[0] === 'string') {
      return [answers[0], answers[1]]
        .map((value) => moment(value))
        .sort((a, b) => a.diff(b)) // values not guaranteed to be in order, sort is necessary
        .map((date) => date.format(PILL_DATE_FORMAT))
        .join(' to ');
    }

    return answers[0]?.date_range
      ?.map((value) => moment(value).format(PILL_DATE_FORMAT))
      .join(' to ');
  }

  const firstAnswerAttribute = prompt?.attributes?.find(
    (attribute) => attribute?.field === answers[0].attributeId
  );

  const firstAnswerName = firstAnswerMaxLength
    ? truncateTextWithEllipsis({
        length: answers.length > 1 ? firstAnswerMaxLength : firstAnswerMaxLength + 10,
        text: answers[0].name,
      })
    : answers[0].name;

  const firstAnswer =
    firstAnswerAttribute?.name && prompt?.isPrefixIncluded
      ? `${firstAnswerAttribute?.name}: ${firstAnswerName}`
      : firstAnswerName;

  if (answers.length > 1) {
    return `${firstAnswer} ${prompt.isSortEnabled ? 'then' : 'or'} ${answers.length - 1} more`;
  }
  return firstAnswer;
};

/**
 * gets tooltip content for pill based on current prompt and answers
 * @param {Object} options.prompt
 * @param {PromptAnswer[]} options.promptAnswers group.promptAnswers
 * @return {String}
 */
export const getPromptPillTooltip = ({ prompt, promptAnswers = [] }) => {
  const answers = promptAnswers
    .filter(({ _delete, promptId }) => !_delete && promptId === prompt.id)
    .map(({ values }) => values)
    .flat(1);

  const isDateRange = surveyDatePromptIds.includes(prompt?.insightsKey);
  const isPurchaseVolume = prompt?.insightsKey === 'purchase_volume';

  if (answers.length <= 1 || isDateRange || isPurchaseVolume) return null;

  return answers.map((value, index) => {
    return (
      <div key={`selection-${index}`}>
        <small>
          {getPromptPillValue({
            prompt,
            promptAnswers: [{ promptId: prompt.id, values: [value] }],
          })}
        </small>
      </div>
    );
  });
};
